import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import { Grid, Table, TableBody, TableCell, TableContainer, TableRow } from '@material-ui/core';
import { Pie } from 'react-chartjs-2';
import dayjs from 'dayjs';
import pluralize from 'pluralize';
import { useSelector } from 'react-redux';
import {
  INumberedDocumentView,
  ProjectSummaryType,
  WorkflowStatusType,
} from '../../api-client/autogenerated';
import { getProjectSummaryById } from '../../models/api/project';
import { getProjectState } from '../../features/project/selectors';
import { formatMoney, PAY_APPLICATION_COUNTED_STATUSES, parseDate } from '../../scripts/utils';

const COMPLETED_COLOR = '#4380ED';
const RETAINAGE_COLOR = '#FF9900';
const OUTSTANDING_COLOR = '#A9ECBF';

const useStyles = makeStyles({
  root: {
    width: '100%',
  },
  titleStyle: {
    background: 'linear-gradient(225deg, #00308C 0%, #002366 100%)',
    borderRadius: '4px 4px 0px 0px',
  },
  amount: {
    paddingBottom: 8,
    fontSize: 28,
    fontWeight: 500,
    lineHeight: '32px',
  },
  amountGreen: {
    paddingBottom: 8,
    fontSize: 28,
    fontWeight: 500,
    lineHeight: '32px',
    color: '#188D57',
  },
  amountRed: {
    paddingBottom: 8,
    fontSize: 28,
    fontWeight: 500,
    lineHeight: '32px',
    color: '#D02C14',
  },
  description: {
    fontSize: 16,
    letterSpacing: 1,
    fontWeight: 500,
    textTransform: 'uppercase',
  },
  table: {
    '& td, th': {
      border: '1px solid',
    },
  },
  // Holds the chart (with its legend) alongside the stat squares. Once there is
  // no longer room for a square beside the chart, the squares move below it.
  chartArea: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: 16,
    width: '100%',
    '@media (max-width: 749.95px)': {
      flexDirection: 'column',
    },
  },
  // The pie chart with its legend underneath. The stat squares sit to its right.
  // A zero flex basis keeps the chart from claiming a width that would push the
  // squares onto the next line; it simply takes whatever space they leave.
  chartBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
    flex: '1 1 0',
    minWidth: 0,
    '@media (max-width: 749.95px)': {
      flex: 'none',
      width: '100%',
    },
  },
  statBoxes: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    flexShrink: 0,
  },
  // The card fills its container, so cap the chart rather than letting it
  // stretch with the card. The chart keeps a 1:1 aspect ratio, so capping the
  // width caps the height too.
  pieContainer: {
    width: '100%',
    maxWidth: 300,
    flexShrink: 0,
  },
  // Kept near the chart's width so the label and amount do not drift to
  // opposite ends of a wide card.
  legend: {
    width: '100%',
    maxWidth: 400,
  },
  // Matches the stat squares in DashboardDoughnut.
  statBox: {
    width: 154,
    height: 154,
    backgroundColor: 'rgba(9,71,185,0.4)',
    borderRadius: 8,
    display: 'grid',
    placeItems: 'center',
    padding: 10,
    boxSizing: 'border-box',
    color: '#464546',
    border: '1px solid',
  },
  // Swatch, label, and value all share this grid across every row so labels and
  // values line up in their own columns regardless of whether a row has a swatch.
  legendRow: {
    display: 'grid',
    gridTemplateColumns: '14px 1fr auto',
    columnGap: 8,
    alignItems: 'center',
    marginBottom: 8,
  },
  legendSwatch: {
    width: 14,
    height: 14,
    borderRadius: 2,
  },
});

type Props = {
  documents?: INumberedDocumentView[];
};

export default function PayApplicationsSummary(props: Props) {
  const { documents } = props;

  const classes = useStyles();
  const project = useSelector(getProjectState);

  const [projectSummary, setProjectSummary] = useState<ProjectSummaryType>();

  useEffect(() => {
    if (project) {
      getProjectSummaryById(project.id)
        .then(setProjectSummary)
        .catch(() => setProjectSummary(undefined));
    }
  }, [project]);

  const getClass = (amount: number) => {
    if (amount > 0) return classes.amountRed;
    if (amount < 0) return classes.amountGreen;
    return classes.amount;
  };

  const getSign = (amount: number) => {
    if (amount > 0) return '+';
    return '';
  };

  const initialContractAmount = project?.contractAmount ? Number(project.contractAmount) : 0;
  const contractDifference = projectSummary?.costAdded ?? 0;
  const currentContractAmount = initialContractAmount + contractDifference;

  // Cumulative total of the dollar value for final Pay Applications whose review
  // has finished. Hidden (soft-deleted) documents are excluded.
  const countedPayApplications = (documents ?? []).filter(
    (document) =>
      !document.isHidden &&
      !!document.isFinal &&
      !!document.workflowStatus &&
      PAY_APPLICATION_COUNTED_STATUSES.includes(document.workflowStatus),
  );
  const completedFinalTotal = countedPayApplications.reduce(
    (sum, document) => sum + (document.dollarValue ?? 0),
    0,
  );

  const totalRetainage = countedPayApplications.reduce(
    (sum, document) => sum + (document.retainage ?? 0),
    0,
  );

  const outstandingContractValue = currentContractAmount - completedFinalTotal;

  const completedPercent =
    currentContractAmount > 0 ? Math.round((completedFinalTotal / currentContractAmount) * 100) : 0;

  // Contract schedule, including any days added by approved changes, measured
  // from the notice to proceed.
  const initialConstructionDays =
    project?.scheduledEndDate && project?.noticeToProceedDate
      ? parseDate(project.scheduledEndDate).diff(parseDate(project.noticeToProceedDate), 'days')
      : 0;
  const totalConstructionDays = initialConstructionDays + (projectSummary?.daysAdded ?? 0);
  // Not capped at the scheduled duration: once the scheduled end date has passed
  // the elapsed days exceed the total and the percentage goes above 100%.
  const elapsedConstructionDays = project?.noticeToProceedDate
    ? Math.max(dayjs().diff(parseDate(project.noticeToProceedDate), 'days'), 0)
    : 0;
  const elapsedSchedulePercent =
    totalConstructionDays > 0
      ? Math.round((elapsedConstructionDays / totalConstructionDays) * 100)
      : 0;

  // Scheduled end date shifted by any days added through approved changes.
  const currentScheduledEndDate = project?.scheduledEndDate
    ? parseDate(project.scheduledEndDate).add(projectSummary?.daysAdded ?? 0, 'days')
    : null;

  // Liquidated damages, matching DashboardCountdown: once the current scheduled
  // end date has passed without substantial completion, show the running total
  // instead of the daily rate.
  const isOverSchedule =
    !!currentScheduledEndDate &&
    !project?.substantialCompletionDate &&
    dayjs().isAfter(currentScheduledEndDate);
  const daysOverSchedule = isOverSchedule
    ? Math.max(dayjs().diff(currentScheduledEndDate!, 'day'), 0)
    : 0;
  const liquidatedDamagesPerDay = project?.liquidatedDamagesPerDay ?? 0;
  const cumulativeLiquidatedDamages = liquidatedDamagesPerDay * daysOverSchedule;

  const pieData = {
    labels: ['Completed Pay Applications', 'Total Retainage to Date', 'Outstanding Contract Value'],
    datasets: [
      {
        data: [completedFinalTotal, totalRetainage, Math.max(outstandingContractValue, 0)],
        backgroundColor: [COMPLETED_COLOR, RETAINAGE_COLOR, OUTSTANDING_COLOR],
      },
    ],
  };

  const emptyPieData = {
    labels: ['Completed Pay Applications', 'Total Retainage to Date', 'Outstanding Contract Value'],
    datasets: [
      {
        data: [1],
        backgroundColor: ['#EDECEC'],
      },
    ],
  };

  const hasPieData = completedFinalTotal > 0 || totalRetainage > 0 || outstandingContractValue > 0;

  const pieOptions = {
    responsive: true,
    aspectRatio: 1,
    maintainAspectRatio: true,
    legend: {
      display: false,
    },
    tooltips: {
      callbacks: {
        label: (tooltipItem: { index: number }, data: { labels: string[] }) => {
          const value = pieData.datasets[0].data[tooltipItem.index];
          return `${data.labels[tooltipItem.index]}: ${formatMoney(value)}`;
        },
      },
    },
  };

  return (
    <Card className={classes.root}>
      <CardContent className={classes.titleStyle}>
        <h2
          style={{
            textAlign: 'left',
            color: '#FFFFFF',
            margin: 0,
          }}
        >
          Contract Value
        </h2>
      </CardContent>
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} lg={6}>
            <TableContainer>
              <Table className={classes.table}>
                <TableBody>
                  <TableRow>
                    <TableCell align="left">
                      <Typography color="textPrimary" className={classes.description}>
                        Value at NTP
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography color="textPrimary" className={classes.amount}>
                        {formatMoney(initialContractAmount)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow style={{ backgroundColor: '#FFF' }}>
                    <TableCell align="left">
                      <Typography color="textPrimary" className={classes.description}>
                        Cumulative Approved Changes
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography color="textPrimary" className={getClass(contractDifference)}>
                        {getSign(contractDifference)}
                        {formatMoney(contractDifference)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align="left">
                      <Typography color="textPrimary" className={classes.description}>
                        Current Contract Value
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography color="textPrimary" className={classes.amount}>
                        {formatMoney(currentContractAmount)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow style={{ backgroundColor: '#FFF' }}>
                    <TableCell align="left">
                      <Typography color="textPrimary" className={classes.description}>
                        Completed Pay Applications
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        color="textPrimary"
                        className={classes.amount}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        {completedFinalTotal > 0 ? '-' : ''}
                        {formatMoney(completedFinalTotal)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align="left">
                      <Typography color="textPrimary" className={classes.description}>
                        Outstanding Contract Value
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        color="textPrimary"
                        className={
                          outstandingContractValue < 0 ? classes.amountRed : classes.amount
                        }
                      >
                        {formatMoney(outstandingContractValue)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow style={{ backgroundColor: '#FFF' }}>
                    <TableCell align="left">
                      <Typography color="textPrimary" className={classes.description}>
                        Total Retainage to Date
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography color="textPrimary" className={classes.amount}>
                        {formatMoney(totalRetainage)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12} lg={6}>
            <div className={classes.chartArea}>
              <div className={classes.chartBlock}>
                <div className={classes.pieContainer}>
                  <Pie data={hasPieData ? pieData : emptyPieData} options={pieOptions} />
                </div>
                <div className={classes.legend}>
                  {currentScheduledEndDate && (
                    <div className={classes.legendRow}>
                      <div />
                      <Typography color="textPrimary" style={{ fontWeight: 500 }}>
                        Current Scheduled End Date
                      </Typography>
                      <Typography align="right" color="textPrimary" style={{ fontWeight: 500 }}>
                        {currentScheduledEndDate.format('MMMM DD, YYYY')}
                      </Typography>
                    </div>
                  )}
                  <div
                    className={classes.legendRow}
                    style={{ marginBottom: isOverSchedule ? 0 : 8 }}
                  >
                    <div />
                    <Typography color="textPrimary" style={{ fontWeight: 500 }}>
                      {isOverSchedule ? 'Current Liquidated Damages' : 'Liquidated Damages Per Day'}
                    </Typography>
                    <Typography align="right" color="textPrimary" style={{ fontWeight: 500 }}>
                      {formatMoney(
                        isOverSchedule ? cumulativeLiquidatedDamages : liquidatedDamagesPerDay,
                      )}
                    </Typography>
                  </div>
                  {isOverSchedule && (
                    <div className={classes.legendRow}>
                      <div />
                      <Typography color="textPrimary" style={{ fontSize: 13 }}>
                        {formatMoney(liquidatedDamagesPerDay)} per day LD x {daysOverSchedule}{' '}
                        {pluralize('day', daysOverSchedule)}
                      </Typography>
                    </div>
                  )}
                  <div className={classes.legendRow}>
                    <div
                      className={classes.legendSwatch}
                      style={{ backgroundColor: COMPLETED_COLOR }}
                    />
                    <Typography color="textPrimary">Completed Pay Applications</Typography>
                    <Typography align="right" color="textPrimary" style={{ fontWeight: 500 }}>
                      {formatMoney(completedFinalTotal)}
                    </Typography>
                  </div>
                  <div
                    className={classes.legendRow}
                    style={{ marginBottom: project?.payApplicationRetainageDescription ? 0 : 8 }}
                  >
                    <div
                      className={classes.legendSwatch}
                      style={{ backgroundColor: RETAINAGE_COLOR }}
                    />
                    <Typography color="textPrimary">Total Retainage to Date</Typography>
                    <Typography align="right" color="textPrimary" style={{ fontWeight: 500 }}>
                      {formatMoney(totalRetainage)}
                    </Typography>
                  </div>
                  {project?.payApplicationRetainageDescription && (
                    <div className={classes.legendRow}>
                      <div />
                      <Typography color="textPrimary" style={{ fontSize: 13 }}>
                        {project.payApplicationRetainageDescription}
                      </Typography>
                    </div>
                  )}
                  <div className={classes.legendRow}>
                    <div
                      className={classes.legendSwatch}
                      style={{ backgroundColor: OUTSTANDING_COLOR }}
                    />
                    <Typography color="textPrimary">Outstanding Contract Value</Typography>
                    <Typography align="right" color="textPrimary" style={{ fontWeight: 500 }}>
                      {formatMoney(outstandingContractValue)}
                    </Typography>
                  </div>
                </div>
              </div>
              <div className={classes.statBoxes}>
                <div className={classes.statBox}>
                  <div style={{ textAlign: 'center', maxWidth: '100%' }}>
                    <div style={{ marginBottom: 10, fontSize: 14 }}>Complete Pay Applications</div>
                    <div style={{ fontSize: 35, marginBottom: 8, fontWeight: 400 }}>
                      {completedPercent}%
                    </div>
                    <div style={{ fontSize: 14 }}>Of Outstanding Contract Value</div>
                  </div>
                </div>
                <div className={classes.statBox}>
                  <div style={{ textAlign: 'center', maxWidth: '100%' }}>
                    <div style={{ marginBottom: 10, fontSize: 14 }}>Elapsed Contract Schedule</div>
                    <div style={{ fontSize: 35, marginBottom: 8, fontWeight: 400 }}>
                      {elapsedSchedulePercent}%
                    </div>
                    <div style={{ fontSize: 14 }}>
                      {elapsedConstructionDays} / {totalConstructionDays} days
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
